// Funciones de Lógica de Color

// Función para convertir HEX a RGB (compatible con formatos 6 dígitos, ignorando el canal alfa ff)
export function hexToRgb(hex) {
    // Limpia el canal alfa si está presente (ej. #ffd000ff -> #ffd000)
    if (hex.length === 9) {
        hex = hex.slice(0, 7);
    }

    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return {r, g, b};
}

// 1. Calcula la Luminancia Relativa (fórmula WCAG)
export function getLuminance(rgb) {
    const [R, G, B] = [rgb.r, rgb.g, rgb.b].map(v => {
        v /= 255;
        // Conversión sRGB lineal
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });

    // Fórmula de Luminancia (peso de los colores)
    return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

// 2. Decide el color de contraste (blanco o negro)
export function getContrastColor(hex) {
    const rgb = hexToRgb(hex);
    const luminance = getLuminance(rgb);

    // Umbral recomendado para alto contraste.
    // Si la luminancia es alta (color claro), retorna negro.
    return luminance > 0.179 ? '#000000' : '#ffffff';
}

// Función auxiliar para normalizar valores de color
export const normalize = (v) => {
    return Math.max(0, Math.min(1, v));
};

// Convierte HEX a HSL
export function hexToHsl(hex) {
    // 1. Limpiar y convertir a RGB
    if (hex.length === 9) hex = hex.slice(0, 7); // Ignorar canal alfa (FF)

    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // Escala de grises
    } else {
        let d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }

    return {
        h: Math.round(h * 360), // Matiz (Hue) de 0 a 360
        s: normalize(s),      // Saturación (Saturation) de 0 a 1
        l: normalize(l)       // Luminosidad (Lightness) de 0 a 1
    };
}

// Convierte HSL a HEX
export function hslToHex(h, s, l) {
    let r, g, b;
    h /= 360; // Normalizar H a 0-1

    if (s === 0) {
        r = g = b = l; // Escala de grises
    } else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (c) => {
        const hex = Math.round(c * 255).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}


// --- Lógica del Complementario ---

export function getComplementaryColor(baseHex) {
    let {h, s, l} = hexToHsl(baseHex);

    // Calcula el color complementario: Mueve el matiz 180 grados
    h = (h + 180) % 360;

    // Devuelve el nuevo color en formato HEX
    return hslToHex(h, s, l);
}

// --- Funciones de Conversión de Color ---


// Función principal para oscurecer el color
export function darkenColor(baseHex, percent) {
    let {h, s, l} = hexToHsl(baseHex);

    // Reduce la luminosidad (L) por el porcentaje
    // El porcentaje debe convertirse a un valor de 0 a 1 (ej. 15% -> 0.15)
    const reduction = percent / 100;

    // Asegura que la luminosidad no baje de 0
    l = Math.max(0, l - reduction);

    // Devuelve el color oscurecido en formato HEX
    return hslToHex(h, s, l);
}

// --- Función Principal para Aclarar el Color ---

export function lightenColor(baseHex, percent) {
    // Convierte el porcentaje a un valor decimal (ej. 15 -> 0.15)
    const increase = percent / 100;

    // Obtiene los componentes HSL del color base
    let {h, s, l} = hexToHsl(baseHex);

    // Aumenta la luminosidad (L)
    // Asegura que la luminosidad no exceda 1 (o 100%)
    l = Math.min(1, l + increase);

    // Devuelve el color aclarado en formato HEX
    return hslToHex(h, s, l);
}
